import React from "react";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import MasterComponent from "../../../components/Forms";
import { constants } from "../../../constants/common";
import { ComboConfig, MasterConfigs } from "../../../interfaces/entity";
import moment from "moment";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { Button, Flex, Tag } from "antd";
import DrawerComponent from "../../../components/Forms/Drawer";
import { EditOutlined, PhoneOutlined } from "@ant-design/icons";
import CallLogsDetails from "./CallLogsDetails";

const CallLogs: React.FC = () => {
    const [refType, setRefType] = React.useState<string>(null);
    const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
    const [selectedCallLog, setSelectedCallLog] = React.useState<any>(null);

    const config: MasterConfigs = {
        api: () => `/call-logs`,
        title: "Call Logs",
        modalTitle: (e) => `${e} Call Log`,
        isActionTab: true,
        onDataLoad: (data) => {
            setRefType(data?.refType || null);
        },
        getColumns: () => [
            {
                title: "Subject",
                dataIndex: "subject",
                key: "subject",
                render: (text, record) => (
                    <a
                        onClick={() => {
                            setSelectedCallLog(record);
                            setOpenDrawer(true);
                        }}
                    >
                        {text}
                    </a>
                )
            },
            {
                title: "Phone Number",
                dataIndex: "phoneNumber",
                key: "phoneNumber",
                render: (text) => (
                    <span>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {text}
                    </span>
                )
            },
            {
                title: "Call Type",
                dataIndex: "callType",
                key: "callType",
                render: (text) => (
                    <Tag color={text === 'Inbound' ? 'blue' : 'green'}>
                        {text}
                    </Tag>
                )
            },
            {
                title: "Call Status",
                dataIndex: "callStatus",
                key: "callStatus",
                render: (text) => {
                    const statusColors: { [key: string]: string } = {
                        'Completed': 'green',
                        'No Answer': 'orange',
                        'Busy': 'yellow',
                        'Failed': 'red',
                        'Missed': 'red'
                    };
                    return (
                        <Tag color={statusColors[text] || 'default'}>
                            {text}
                        </Tag>
                    );
                }
            },
            {
                title: "Call Date",
                dataIndex: "callDate",
                key: "callDate",
                render: (text) => moment(text).format('DD MMMM YYYY HH:mm')
            },
            {
                title: "Duration",
                dataIndex: "callDuration",
                key: "callDuration",
                render: (text) => {
                    if (!text || text === 0) return '-';
                    const minutes = Math.floor(text / 60);
                    const seconds = text % 60;
                    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                }
            },
            {
                title: "Related To",
                dataIndex: "refId",
                key: "refId",
                render: (text, record) => {
                    if (['Leads', 'Contacts', 'User', 'ShortlistedCandidatesForInterview'].includes(record.refType)) {
                        return (
                            <SmallProfileCard
                                name={constants.GET_FULL_NAME(text)}
                                avatar={text?.profilePhoto || text?.avatar}
                            />
                        );
                    } else if (record.refType === 'Businesses') {
                        return text?.businessName || 'N/A';
                    } else if (record.refType === 'Deals') {
                        return text?.dealName || 'N/A';
                    }
                    return 'N/A';
                }
            },
            {
                title: "Call Owner",
                dataIndex: "callOwner",
                key: "callOwner",
                render: (text) => (
                    <SmallProfileCard
                        name={constants.GET_FULL_NAME(text)}
                        avatar={text?.avatar}
                    />
                )
            },
            {
                title: "Created At",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (text) => moment(text).format('LLL')
            },
        ],
        getFormColumns: () => [
            {
                label: 'Call Owner',
                name: 'callOwner',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Call Owner is required' }],
            },
            {
                span: 16,
            },
            {
                label: "Subject",
                name: "subject",
                type: "text",
                span: 16,
                rules: [{ required: true, message: 'Subject is required' }]
            },
            {
                label: "Phone Number",
                name: "phoneNumber",
                type: "text",
                span: 16,
                rules: [{ required: true, message: 'Phone Number is required' }]
            },
            {
                label: "Call Type",
                name: "callType",
                type: "select",
                children: [
                    { name: 'Inbound', value: 'Inbound' },
                    { name: 'Outbound', value: 'Outbound' }
                ]
            },
            {
                label: "Call Date",
                name: "callDate",
                type: "date",
                rules: [{ required: true, message: 'Call Date is required' }]
            },
            {
                label: "Call Status",
                name: "callStatus",
                type: "select",
                children: [
                    { name: 'Completed', value: 'Completed' },
                    { name: 'No Answer', value: 'No Answer' },
                    { name: 'Busy', value: 'Busy' },
                    { name: 'Failed', value: 'Failed' },
                    { name: 'Missed', value: 'Missed' }
                ]
            },
            {
                label: "Call Duration (seconds)",
                name: "callDuration",
                type: "number"
            },
            {
                label: 'Call For',
                name: 'refType',
                type: 'select',
                children: constants.TASK_FOR.map(type => ({ name: type, value: type })),
                selectProps: {
                    onChange: (value) => {
                        setRefType(value);
                    }
                },
                rules: [{ required: true, message: 'Call For is required' }]
            },
            {
                label: "Call For ID",
                name: "refId",
                type: "select",
                isCombo: true,
                rules: [{ required: true, message: 'Call For ID is required' }],
            },
            {
                span: 16,
            },
            {
                label: "Description",
                name: "description",
                type: "textarea",
                span: 16
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'callOwner',
                idKey: '_id',
                render: (text) => (
                    <ProfileCard
                        size={30}
                        name={constants.GET_FULL_NAME(text)}
                        avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                        role={null}
                    />
                )
            },
            refType && {
                api: `/tasks-management/ref-types/list?refType=${refType}`,
                name: 'refId',
                idKey: '_id',
                render: (text) => text.name,
                rerender: [refType]
            }
        ].filter(Boolean) as ComboConfig[],
        actions: [
            {
                actionType: "Add",
                name: "Add Call Log",
            }
        ]
    };

    const handleEditClick = (callLog: any) => {
        setSelectedCallLog(callLog);
        setOpenDrawer(true);
    };

    return (
        <div>
            <MasterComponent config={config} />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={selectedCallLog?.subject || 'Call Log Details'}
                            avatarUrl={null}
                            role={`Call ${selectedCallLog?.callType || ''} - ${selectedCallLog?.callStatus || ''}`}
                        />
                        <div className="flex gap-2">
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEditClick(selectedCallLog)}
                            >
                                Edit
                            </Button>
                        </div>
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%',
                    style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <CallLogsDetails callLog={selectedCallLog} />
            </DrawerComponent>
        </div>
    );
};

export default CallLogs;
